import { useEffect, useMemo, useRef, useState } from 'react';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import {
  useUniqueStatus,
  useUniqueDeaths,
  type UniqueStatusRow,
} from '@/hooks/useUniquesHistory';
import '@/styles/battlepass-layout.css';
import '@/styles/uniques-history-layout.css';

type StatusSort = 'none' | 'alive-first' | 'dead-first';

function RefreshIcon() {
  return (
    <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7c1.93 0 3.68.78 4.95 2.05A7 7 0 1 1 5 12H3a9 9 0 1 0 14.65-5.65z" />
    </svg>
  );
}

function SortUpIcon() {
  return (
    <svg width="9" height="9" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 4l-8 8h16z" />
    </svg>
  );
}

function SortDownIcon() {
  return (
    <svg width="9" height="9" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 20l8-8H4z" />
    </svg>
  );
}

function sortStatusRows(rows: UniqueStatusRow[], sort: StatusSort): UniqueStatusRow[] {
  if (sort === 'none') return rows;
  return [...rows].sort((a, b) => {
    const aAlive = a.status === 'Alive' ? 1 : 0;
    const bAlive = b.status === 'Alive' ? 1 : 0;
    return sort === 'alive-first' ? bAlive - aAlive : aAlive - bAlive;
  });
}

function StatusTable({
  rows,
  isLoading,
  isError,
  onRefresh,
  refreshing,
}: {
  rows: UniqueStatusRow[];
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const { t } = useLocale();
  const [statusSort, setStatusSort] = useState<StatusSort>('none');

  const showLoading = isLoading && rows.length === 0;
  const sortedRows = useMemo(() => sortStatusRows(rows, statusSort), [rows, statusSort]);

  return (
    <div className="unique-table-section">
      <div className="unique-table-wrapper">
        <table className="unique-table">
          <thead>
            <tr>
              <th className="unique-th-name">
                <span className="unique-th-label">{t('unique_table_name')}</span>
                <button
                  type="button"
                  id="refresh-uniques-btn"
                  className={`unique-refresh-btn${refreshing ? ' refreshing' : ''}`}
                  title={t('unique_refresh')}
                  onClick={onRefresh}
                  aria-label={t('unique_refresh')}
                >
                  <RefreshIcon />
                </button>
              </th>
              <th>{t('unique_table_character')}</th>
              <th className="unique-th-status">
                <span className="unique-th-label">{t('unique_table_status')}</span>
                <span className="unique-sort-btns">
                  <button
                    type="button"
                    className={`unique-sort-btn${statusSort === 'alive-first' ? ' active' : ''}`}
                    title={t('unique_sort_alive_first')}
                    aria-label={t('unique_sort_alive_first')}
                    onClick={() =>
                      setStatusSort((prev) => (prev === 'alive-first' ? 'none' : 'alive-first'))
                    }
                  >
                    <SortUpIcon />
                  </button>
                  <button
                    type="button"
                    className={`unique-sort-btn${statusSort === 'dead-first' ? ' active' : ''}`}
                    title={t('unique_sort_dead_first')}
                    aria-label={t('unique_sort_dead_first')}
                    onClick={() =>
                      setStatusSort((prev) => (prev === 'dead-first' ? 'none' : 'dead-first'))
                    }
                  >
                    <SortDownIcon />
                  </button>
                </span>
              </th>
              <th>{t('unique_table_map')}</th>
              <th>{t('unique_table_time')}</th>
            </tr>
          </thead>
          <tbody>
            {showLoading ? (
              <tr>
                <td colSpan={5} className="unique-empty-cell">
                  {t('loading_data')}
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={5} className="unique-empty-cell">
                  {t('unique_load_error')}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="unique-empty-cell">
                  {t('unique_no_data')}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.id}>
                  <td className="unique-name-td">
                    {row.uniqueName}
                  </td>
                  <td>
                    {row.charName128}
                  </td>
                  <td>
                    <span className={`unique-status ${row.status.toLowerCase()}`}>
                      {row.status === 'Alive' ? t('unique_status_alive') : t('unique_status_dead')}
                    </span>
                  </td>
                  <td>{row.map}</td>
                  <td>{row.lastUpdate}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeathHistoryModal({ onClose }: { onClose: () => void }) {
  const { t } = useLocale();
  const { data: rows = [], isLoading, isError, refetch, isFetching } = useUniqueDeaths();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  function handleClose() {
    dialogRef.current?.close();
    onClose();
  }
  return (
    <dialog ref={dialogRef} className="unique-deaths-dialog silk-modal--aaa"
      aria-labelledby="unique-deaths-title" onCancel={event => { event.preventDefault(); handleClose(); }}
      onClick={event => { if (event.target === event.currentTarget) handleClose(); }}>
      <div className="reset-spin-modal-content">
        <div className="reset-spin-modal-header unique-deaths-header">
          <h3 id="unique-deaths-title">{t('unique_death_history')}</h3>
          <button type="button" className="battlepass-exp-modal-close" aria-label={t('silk_confirm_close')} onClick={handleClose}>×</button>
        </div>
        <div className="unique-deaths-scroll">
          <table className="unique-table">
            <thead><tr>
              <th>{t('unique_table_name')}</th><th>{t('unique_table_character')}</th>
              <th>{t('unique_table_map')}</th><th>{t('unique_table_time')}</th>
            </tr></thead>
            <tbody>
              {isLoading || isError || !rows.length ? <tr><td colSpan={4} className="unique-empty-cell" role={isError ? 'alert' : 'status'}>
                {t(isLoading ? 'loading_data' : isError ? 'unique_load_error' : 'unique_logs_empty')}
              </td></tr> : rows.map(row => <tr key={row.id}>
                <td className="unique-name-td">{row.uniqueName}</td><td>{row.charName128}</td>
                <td>{row.map}</td><td>{row.eventAt}</td>
              </tr>)}
            </tbody>
          </table>
        </div>
        <div className="reset-spin-modal-footer">
          <button type="button" className="reset-spin-btn-cancel" disabled={isFetching} onClick={() => void refetch()}>{t('unique_refresh')}</button>
          <button type="button" className="reset-spin-btn-confirm" onClick={handleClose}>{t('silk_confirm_close')}</button>
        </div>
      </div>
    </dialog>
  );
}

export function UniquesHistoryPage() {
  const { t } = useLocale();
  const {
    data: statusRows = [],
    isLoading: statusLoading,
    isError: statusError,
    isFetching: statusFetching,
    refetch: refetchStatus,
  } = useUniqueStatus();
  const [logsOpen, setLogsOpen] = useState(false);
  return (
    <div className="container-ingame fade-in">
      <div className="calendar-container uniques-history-page">
        <SimpleMenu title={t('unique')}>
          <button type="button" className="unique-info-btn" title={t('unique_death_history')}
            aria-label={t('unique_death_history')} aria-haspopup="dialog" onClick={() => setLogsOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <circle cx="12" cy="12" r="9" /><path d="M12 11v6" /><circle cx="12" cy="7" r=".8" fill="currentColor" />
            </svg>
          </button>
        </SimpleMenu>

        <div className="unique-content">
          <StatusTable
            rows={statusRows}
            isLoading={statusLoading}
            isError={statusError}
            onRefresh={() => void refetchStatus()}
            refreshing={statusFetching}
          />
        </div>
        {logsOpen ? <DeathHistoryModal onClose={() => setLogsOpen(false)} /> : null}
      </div>
    </div>
  );
}

export default UniquesHistoryPage;
